import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  IconRobot,
  IconSend,
  IconHistory,
  IconChevronDown,
  IconCheck,
  IconRefresh,
  IconAlertTriangle,
  IconInfoCircle,
  IconKey,
  IconToggleLeft,
  IconX,
} from "@tabler/icons-react";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import UserContext from "../../../contexts/UserContext";
import useAgentTesting from "../../../services/AgentTestService";
import {
  GetAgentVersions,
  PrepareAgentForTest,
  GetAgentStats,
} from "../../../services/WaBottyServices";
import {
  ActionButton,
  ActionButtons,
  AgentInfo,
  AgentName,
  AgentSubtitle,
  ChatContainer,
  ChatHeader,
  CurrentBadge,
  ErrorBadge,
  FunctionBadge,
  HeaderActions,
  HeaderContent,
  InfoBar,
  InfoText,
  InputContainer,
  MessageBubble,
  MessageInput,
  MessageInputWrapper,
  MessagesContainer,
  MessagesList,
  MessageText,
  MessageTime,
  RobotIcon,
  SendButton,
  TypingDots,
  TypingIndicator,
  VersionDate,
  VersionDropdown,
  VersionDropdownHeader,
  VersionInfo,
  VersionLabel,
  VersionOption,
  VersionSelectorButton,
  VersionSelectorContainer,
  WarningBadge,
} from "./agentChatStyles";
import { CloseButton, Alert } from "react-bootstrap";

// ==========================================
// FUNÇÕES AUXILIARES PARA QUEBRA DE MENSAGENS
// ==========================================

/**
 * Processa mensagens que contêm URLs de imagem e as divide em partes separadas
 */
const processImageBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string")
    return [{ type: "text", content: messageText }];

  const imageUrlRegex = /(https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|bmp|svg))/gi;
  const imageUrls = messageText.match(imageUrlRegex);

  if (!imageUrls || imageUrls.length === 0) {
    return [{ type: "text", content: messageText }];
  }

  const parts = [];
  let remainingText = messageText;

  imageUrls.forEach((imageUrl) => {
    const splitResult = remainingText.split(imageUrl);

    if (splitResult[0].trim()) {
      parts.push({
        type: "text",
        content: splitResult[0].trim(),
      });
    }

    parts.push({
      type: "image",
      content: imageUrl,
    });

    remainingText = splitResult.slice(1).join(imageUrl);
  });

  if (remainingText.trim()) {
    parts.push({
      type: "text",
      content: remainingText.trim(),
    });
  }

  return parts;
};

/**
 * Processa mensagens que contêm a tag [[break]] e as divide em partes separadas
 */
const processMessageBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string") return [messageText];

  if (!messageText.includes("[[break]]")) {
    return [messageText];
  }

  return messageText
    .split("[[break]]")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

/**
 * Processa todas as quebras ([[break]] + imagens)
 */
const processAllBreaks = (messageText) => {
  if (!messageText || typeof messageText !== "string")
    return [{ type: "text", content: messageText }];

  const breakParts = processMessageBreaks(messageText);
  const allParts = [];

  breakParts.forEach((part) => {
    const imageParts = processImageBreaks(part);
    allParts.push(...imageParts);
  });

  return allParts;
};

/**
 * Simula digitação com delay baseado no tamanho do texto
 */
const getTypingDelay = (content, contentType = "text") => {
  const baseDelay = 800;

  if (contentType === "image") {
    return 1200;
  }

  const charDelay = content.length * 25;
  const maxDelay = 2500;

  return Math.min(baseDelay + charDelay, maxDelay);
};

/**
 * Promessa para simular delay
 */
const simulateTyping = (duration) => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

// ==========================================
// COMPONENTE PRINCIPAL REFATORADO
// ==========================================

const AgentChat = ({ agentData, onClose }) => {
  const { user, bottyData } = useContext(UserContext);
  const {
    sendMessage,
    loading: serviceLoading,
    error: serviceError,
    setError,
  } = useAgentTesting();

  // Estados principais
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Estados para versões e configurações
  const [agentVersions, setAgentVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [agentStats, setAgentStats] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [initializationError, setInitializationError] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const scrollbarRef = useRef(null);

  // ==========================================
  // VALIDAÇÕES CRÍTICAS DO AGENTE
  // ==========================================

  const agentValidation = useMemo(() => {
    if (!agentData) {
      return {
        isValid: false,
        errors: ["Dados do agente não fornecidos"],
        warnings: [],
      };
    }

    const errors = [];
    const warnings = [];

    // Validações críticas
    if (!agentData.id) {
      errors.push("ID do agente não encontrado");
    }

    if (!agentData.api_key || agentData.api_key.trim() === "") {
      errors.push("API Key não configurada");
    } else if (!agentData.api_key.startsWith("sk-")) {
      errors.push("API Key inválida (deve começar com 'sk-')");
    }

    // if (!agentData.is_active) {
    //   errors.push("Agente está inativo");
    // }

    if (!agentData.agent_name || agentData.agent_name.trim() === "") {
      warnings.push("Nome do agente não definido");
    }

    if (!agentData.generated_prompt && !agentData.core_prompt) {
      warnings.push("Prompt não configurado");
    }

    if (!agentData.version_active || agentData.version_active < 1) {
      warnings.push("Versão ativa inválida");
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      canTest: errors.length === 0,
    };
  }, [agentData]);

  // ==========================================
  // SISTEMA DE AUTO-FOCUS REFATORADO E ROBUSTO
  // ==========================================

  // Função robusta para focar o input
  const focusInput = useCallback(() => {
    // Verifica se as condições estão adequadas para focar
    if (!inputRef.current) return;
    if (isInitializing) return;
    if (!agentValidation.canTest) return;

    try {
      // Remove qualquer foco anterior
      if (
        document.activeElement &&
        document.activeElement !== inputRef.current
      ) {
        document.activeElement.blur();
      }

      // Foca o input
      inputRef.current.focus();

      // Garante que o cursor está no final do texto
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);

      console.log("✅ Input focado com sucesso");
    } catch (error) {
      console.warn("⚠️ Erro ao focar input:", error);
    }
  }, [isInitializing, agentValidation.canTest]);

  // Hook para focar após inicialização
  useEffect(() => {
    if (!isInitializing && agentValidation.canTest) {
      const timer = setTimeout(() => {
        focusInput();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isInitializing, agentValidation.canTest, focusInput]);

  // Hook CRÍTICO - monitora quando isLoading muda de true para false
  useEffect(() => {
    // Se acabou de sair do loading, foca o input
    if (!isLoading && !isInitializing && agentValidation.canTest) {
      const timer = setTimeout(() => {
        focusInput();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isInitializing, agentValidation.canTest, focusInput]);

  // ==========================================
  // INICIALIZAÇÃO DO AGENTE ESPECÍFICO
  // ==========================================

  useEffect(() => {
    if (agentData && agentData.id) {
      initializeAgentForTesting();
    } else {
      setInitializationError("Agente não selecionado ou dados inválidos");
      setIsInitializing(false);
    }
  }, [agentData]);

  const initializeAgentForTesting = async () => {
    setIsInitializing(true);
    setInitializationError(null);
    setError(null);

    try {
      console.log("🚀 Inicializando teste para agente:", agentData.agent_name);
      // Validações críticas primeiro
      if (!agentValidation.isValid) {
        throw new Error(
          `Agente não pode ser testado: ${agentValidation.errors.join(", ")}`
        );
      }

      // Preparar dados específicos do agente usando nova API
      let testData;
      try {
        testData = await PrepareAgentForTest(agentData.id);
      } catch (error) {
        // Fallback: usar dados do props se API falhar
        console.warn(
          "⚠️ API PrepareAgentForTest falhou, usando dados do props"
        );
        testData = {
          ...agentData,
          ready_for_test: agentValidation.canTest,
        };
      }

      // Buscar versões específicas do agente
      let versions = [];
      try {
        const versionData = await GetAgentVersions(agentData.id);
        versions = versionData.versions || [];
      } catch (error) {
        console.warn("⚠️ Erro ao buscar versões, usando versão atual");
        versions = [
          {
            version: agentData.version_active || 1,
            is_active: true,
            is_current: true,
            label: `v.${agentData.version_active || 1}`,
            id: agentData.id,
            created_at: agentData.updated_at || new Date().toISOString(),
          },
        ];
      }

      // Buscar estatísticas se disponível
      let stats = null;
      try {
        stats = await GetAgentStats(agentData.id);
      } catch (error) {
        console.warn("⚠️ Erro ao buscar estatísticas");
      }

      // Configurar estados
      setAgentVersions(versions);
      setAgentStats(stats);
      setValidationResult(agentValidation);

      // Definir versão selecionada (ativa por padrão)
      const activeVersion = versions.find((v) => v.is_active) || versions[0];
      setSelectedVersion(activeVersion);

      // Mensagem de boas-vindas específica do agente
      const welcomeMessage = createWelcomeMessage(testData, agentValidation);
      setMessages([welcomeMessage]);

      console.log("✅ Agente inicializado com sucesso para testes");
    } catch (error) {
      console.error("❌ Erro ao inicializar agente:", error);
      setInitializationError(error.message);

      const errorMessage = {
        id: Date.now(),
        text: `❌ Erro de inicialização: ${error.message}`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
        isSystem: true,
        messageType: "text",
      };
      setMessages([errorMessage]);
    } finally {
      setIsInitializing(false);
    }
  };

  const createWelcomeMessage = (testData, validation) => {
    let welcomeText = "";

    if (validation.errors.length > 0) {
      welcomeText = `⚠️ Agente "${agentData.agent_name}" carregado com ${validation.errors.length} erros críticos. Teste não disponível.`;
    } else if (validation.warnings.length > 0) {
      welcomeText = `⚠️ Agente "${agentData.agent_name}" carregado com ${validation.warnings.length} avisos. Teste disponível com limitações.`;
    } else {
      welcomeText = `✅ Agente "${agentData.agent_name}" carregado e pronto para testes!`;
    }

    return {
      id: Date.now(),
      text: welcomeText,
      isUser: false,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      isSystem: true,
      messageType: "text",
    };
  };

  // ==========================================
  // SCROLL E FOCO
  // ==========================================

  const scrollToBottom = useCallback(() => {
    if (scrollbarRef.current) {
      scrollbarRef.current._container.scrollTop =
        scrollbarRef.current._container.scrollHeight;
    }
  }, []);

  useEffect(() => {
    setTimeout(() => scrollToBottom(), 100);
  }, [messages, scrollToBottom]);

  // ==========================================
  // PROCESSAMENTO DE MENSAGENS MÚLTIPLAS
  // ==========================================

  const processMultiPartMessage = async (messageParts) => {
    for (let i = 0; i < messageParts.length; i++) {
      const part = messageParts[i];
      const content = part.content || part;
      const contentType = part.type || "text";

      if (i > 0) {
        const delay = getTypingDelay(content, contentType);
        await simulateTyping(delay);
      }

      const aiMessage = {
        id: Date.now() + i + 1 + Math.random() * 1000,
        text: content,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isPartOf: messageParts.length > 1,
        partIndex: i + 1,
        totalParts: messageParts.length,
        messageType: contentType,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  // ==========================================
  // RENDERIZAÇÃO DE CONTEÚDO
  // ==========================================

  const renderMessageContent = (text, messageType = "text") => {
    if (typeof text !== "string") return null;

    const cleanText = text.replace(/\[\[break\]\]/g, "").trim();

    if (messageType === "image") {
      return (
        <div
          style={{
            display: "block",
            width: "100%",
            margin: "12px 0",
            textAlign: "left",
          }}
        >
          <img
            src={cleanText}
            alt="Imagem"
            style={{
              maxWidth: "250px",
              width: "auto",
              height: "auto",
              borderRadius: "12px",
              display: "block",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
            onError={(e) => {
              e.target.outerHTML = `
                <div style="color: #666; font-size: 12px; padding: 12px; background: #f8f9fa; border-radius: 8px; border: 1px dashed #dee2e6; max-width: 250px; display: block;">
                  📷 Imagem não pôde ser carregada<br/>
                  <a href="${cleanText}" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: underline;">
                    Ver imagem original
                  </a>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    if (cleanText.startsWith("audio:")) {
      const audioUrl = cleanText.split("audio:")[1];
      return (
        <audio controls style={{ maxWidth: "100%", marginTop: "4px" }}>
          <source src={audioUrl} type="audio/wav" />
          Seu navegador não suporta o elemento de áudio.
        </audio>
      );
    }

    return (
      <span>
        {cleanText.split("\n").map((line, index) => (
          <span key={index}>
            {line}
            {index < cleanText.split("\n").length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };

  // ==========================================
  // ENVIO DE MENSAGENS ESPECÍFICAS DO AGENTE
  // ==========================================

  const handleSendMessage = async () => {
    if (
      !inputMessage.trim() ||
      isLoading ||
      !selectedVersion ||
      !agentValidation.canTest ||
      isInitializing
    )
      return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      messageType: "text",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      console.log(
        `📤 Enviando mensagem para agente ${agentData.agent_name}:`,
        inputMessage
      );

      // Payload específico do agente
      const messagePayload = {
        id: agentData.id,
        uid: bottyData.uid,
        phone: user.phone,
        message: inputMessage,
        account_id: user.account_id,
        agent_id: agentData.id, // ✅ ID específico do agente
        version_active: selectedVersion.version || agentData.version_active, // ✅ Versão específica
        internal: true,
      };

      const response = await sendMessage(messagePayload);

      if (response) {
        let responseText = "";
        if (response.status === 200 && response.data) {
          responseText = response.data;
        } else if (response.message) {
          responseText = response.message;
        } else {
          responseText = "Resposta recebida";
        }

        console.log(
          `📥 Resposta do agente ${agentData.agent_name}:`,
          responseText
        );

        const messageParts = processAllBreaks(responseText);

        if (messageParts.length === 1 && messageParts[0].type === "text") {
          const aiMessage = {
            id: Date.now() + 1,
            text: messageParts[0].content,
            isUser: false,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            messageType: "text",
          };
          setMessages((prev) => [...prev, aiMessage]);
        } else {
          await processMultiPartMessage(messageParts);
        }
      } else {
        throw new Error(serviceError || "Erro na comunicação com o agente");
      }
    } catch (error) {
      console.error(
        `❌ Erro ao comunicar com agente ${agentData.agent_name}:`,
        error
      );

      let errorText = `Falha na comunicação com ${agentData.agent_name}`;

      if (error.response?.data?.error) {
        errorText = error.response.data.error;
      } else if (serviceError) {
        errorText = serviceError;
      } else if (error.message) {
        errorText = error.message;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: `❌ Erro: ${errorText}`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
        messageType: "text",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // O useEffect vai capturar a mudança de isLoading e focar automaticamente
      console.log("📝 Finalizando envio - aguardando auto-focus...");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ==========================================
  // GERENCIAMENTO DE VERSÕES ESPECÍFICAS
  // ==========================================

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    setShowVersionSelector(false);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: `✅ Agora testando ${
          version.is_current ? "versão atual" : `versão ${version.version}`
        } do agente ${agentData.agent_name}.`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isSystem: true,
        messageType: "text",
      },
    ]);

    // Focar após trocar versão
    setTimeout(() => {
      focusInput();
    }, 200);
  };

  const clearConversation = async () => {
    setMessages([]);
    setError(null);

    if (selectedVersion && agentValidation.canTest) {
      try {
        await sendMessage({
          uid: bottyData.uid,
          phone: user.phone,
          message: "resetar",
          account_id: user.account_id,
          agent_id: agentData.id,
          internal: true,
        });
      } catch (error) {
        console.warn("⚠️ Erro ao resetar conversa:", error);
      }

      const welcomeMessage = createWelcomeMessage(agentData, agentValidation);
      setMessages([welcomeMessage]);
    }

    // Focar após limpar conversa
    setTimeout(() => {
      focusInput();
    }, 300);
  };

  // ==========================================
  // RENDERIZAÇÃO DE COMPONENTES
  // ==========================================

  const renderMessageBubble = (message) => {
    const hasImage = message.messageType === "image";

    return (
      <MessageBubble
        key={message.id}
        isUser={message.isUser}
        isError={message.isError}
        isSystem={message.isSystem}
      >
        {hasImage ? (
          <div
            style={{
              width: "100%",
              display: "block",
              lineHeight: "1.4",
            }}
          >
            {renderMessageContent(message.text, message.messageType)}
          </div>
        ) : (
          <MessageText isError={message.isError} isSystem={message.isSystem}>
            {renderMessageContent(message.text, message.messageType)}
          </MessageText>
        )}

        <MessageTime>
          {message.timestamp}
          {message.isPartOf && (
            <span
              style={{
                marginLeft: "8px",
                fontSize: "10px",
                opacity: 0.6,
                backgroundColor: "rgba(0,0,0,0.1)",
                padding: "1px 4px",
                borderRadius: "3px",
              }}
            >
              {message.partIndex}/{message.totalParts}
              {message.messageType === "image" && " 🖼️"}
            </span>
          )}
        </MessageTime>
      </MessageBubble>
    );
  };

  const renderVersionSelector = () => (
    <VersionSelectorContainer>
      <VersionSelectorButton
        onClick={() => setShowVersionSelector(!showVersionSelector)}
        isActive={showVersionSelector}
        disabled={!agentValidation.canTest}
      >
        <IconHistory size={16} />
        <span>
          {selectedVersion?.is_current
            ? `v${selectedVersion.version || agentData.version_active} (atual)`
            : `v${selectedVersion?.version || agentData.version_active || "?"}`}
        </span>
        <IconChevronDown size={14} />
      </VersionSelectorButton>
    </VersionSelectorContainer>
  );

  const renderValidationAlert = () => {
    if (
      !validationResult ||
      (!validationResult.errors.length && !validationResult.warnings.length)
    ) {
      return null;
    }

    return (
      <div style={{ padding: "12px" }}>
        {validationResult.errors.length > 0 && (
          <Alert variant="danger" className="mb-2">
            <strong>❌ Erros críticos:</strong>
            <ul className="mb-0 mt-1">
              {validationResult.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {validationResult.warnings.length > 0 && (
          <Alert variant="warning" className="mb-0">
            <strong>⚠️ Avisos:</strong>
            <ul className="mb-0 mt-1">
              {validationResult.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    );
  };

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================

  if (!agentData) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <IconRobot size={48} style={{ opacity: 0.3 }} />
        <p>Nenhum agente selecionado para teste</p>
      </div>
    );
  }

  return (
    <ChatContainer isOpen={true}>
      <ChatHeader>
        <HeaderContent>
          <RobotIcon>
            <IconRobot size={24} />
          </RobotIcon>
          <AgentInfo>
            <AgentName>{agentData.agent_name || "Agente"}</AgentName>
            <AgentSubtitle>
              {agentData.agent_job || "Cargo não definido"}
              {!agentData.api_key && (
                <span style={{ color: "#dc3545", marginLeft: "8px" }}>
                  <IconKey size={12} /> Sem API Key
                </span>
              )}
              {!agentData.is_active && (
                <span style={{ color: "#dc3545", marginLeft: "8px" }}>
                  <IconToggleLeft size={12} /> Inativo
                </span>
              )}
            </AgentSubtitle>
          </AgentInfo>
        </HeaderContent>
        <HeaderActions>
          {renderVersionSelector()}
          <ActionButton
            onClick={clearConversation}
            title="Limpar conversa"
            disabled={!agentValidation.canTest}
          >
            <IconRefresh size={18} />
          </ActionButton>
          {onClose && (
            <ActionButton onClick={onClose} title="Fechar">
              <IconX size={18} />
            </ActionButton>
          )}
        </HeaderActions>
      </ChatHeader>

      {/* Info Bar com status do agente */}
      {selectedVersion && (
        <InfoBar
          hasIssues={
            validationResult &&
            (validationResult.errors.length > 0 ||
              validationResult.warnings.length > 0)
          }
        >
          <InfoText>
            <strong>Testando:</strong>{" "}
            {selectedVersion.is_current
              ? "Versão atual"
              : `Versão ${
                  selectedVersion.version || selectedVersion.version_number
                }`}
            {agentData.functions_enabled && (
              <FunctionBadge>Funções ON</FunctionBadge>
            )}
            {validationResult?.errors.length > 0 && (
              <ErrorBadge
                title={`${validationResult.errors.length} erros encontrados`}
              >
                <IconAlertTriangle size={12} />
                {validationResult.errors.length}
              </ErrorBadge>
            )}
            {validationResult?.warnings.length > 0 && (
              <WarningBadge
                title={`${validationResult.warnings.length} avisos`}
              >
                <IconInfoCircle size={12} />
                {validationResult.warnings.length}
              </WarningBadge>
            )}
          </InfoText>
        </InfoBar>
      )}

      {/* Alertas de validação */}
      {renderValidationAlert()}

      <MessagesContainer>
        <PerfectScrollbar ref={scrollbarRef}>
          <MessagesList>
            {isInitializing ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <TypingIndicator>
                  <TypingDots>
                    <span></span>
                    <span></span>
                    <span></span>
                  </TypingDots>
                  <MessageTime>Inicializando agente...</MessageTime>
                </TypingIndicator>
              </div>
            ) : (
              messages.map((message) => renderMessageBubble(message))
            )}

            {isLoading && (
              <TypingIndicator>
                <TypingDots>
                  <span></span>
                  <span></span>
                  <span></span>
                </TypingDots>
                <MessageTime>
                  {agentData.agent_name} está processando...
                </MessageTime>
              </TypingIndicator>
            )}
            <div ref={messagesEndRef} />
          </MessagesList>
        </PerfectScrollbar>
      </MessagesContainer>

      <InputContainer>
        <MessageInputWrapper>
          <MessageInput
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              agentValidation.canTest
                ? `Teste o agente ${agentData.agent_name}...`
                : "Agente não disponível para teste"
            }
            rows={1}
            disabled={!agentValidation.canTest || isLoading || isInitializing}
          />
          <ActionButtons>
            <SendButton
              onClick={handleSendMessage}
              disabled={
                !inputMessage.trim() ||
                isLoading ||
                !agentValidation.canTest ||
                isInitializing
              }
              title={
                !agentValidation.canTest
                  ? "Configure API Key e ative o agente primeiro"
                  : "Enviar mensagem"
              }
            >
              <IconSend size={18} />
            </SendButton>
          </ActionButtons>
        </MessageInputWrapper>
      </InputContainer>
    </ChatContainer>
  );
};

export default AgentChat;
